import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Autorenew as RotateIcon,
  Block as RevokeIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  MoreVert as MoreIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  QrCode2 as QrIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { tenantAdminAPI } from '../../services/api';
import JoinQrDialog from './JoinQrDialog';

const DEFAULT_EXPIRY_DAYS = 30;
const DEFAULT_MAX_EXPIRY_DAYS = 180;

const STATUS_META = {
  active: { label: 'Active', color: 'success' },
  paused: { label: 'Paused', color: 'warning' },
  full: { label: 'Full', color: 'info' },
  expired: { label: 'Expired', color: 'default' },
  revoked: { label: 'Revoked', color: 'error' },
};

const ROLE_OPTIONS = [
  { value: 'student', label: 'Student' },
  { value: 'instructor', label: 'Instructor' },
];

const buildEmptyForm = (expiryDays = DEFAULT_EXPIRY_DAYS) => ({
  label: '',
  role: 'student',
  groupIds: [],
  expiresOn: dayjs().add(expiryDays, 'day').format('YYYY-MM-DD'),
  maxUses: '',
  allowedDomains: '',
});

const formFromLink = (link) => ({
  label: link.label || '',
  role: link.role || 'student',
  groupIds: link.groupIds || [],
  expiresOn: dayjs(link.expiresAt).format('YYYY-MM-DD'),
  maxUses: link.maxUses ? String(link.maxUses) : '',
  allowedDomains: (link.allowedDomains || []).join(', '),
});

// Expiry dates are entered as calendar days, so codes stay valid until the end of that day.
const toExpiryIso = (dateString) => dayjs(dateString).endOf('day').toISOString();

const formatSignups = (link) => (link.maxUses ? `${link.useCount} / ${link.maxUses}` : `${link.useCount}`);

const JoinLinksPanel = ({ groups = [] }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [qrLink, setQrLink] = useState(null);
  const [menuState, setMenuState] = useState({ anchor: null, link: null });
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionPending, setActionPending] = useState('');

  const maxExpiryDays = branding?.defaults?.maxExpiryDays || DEFAULT_MAX_EXPIRY_DAYS;
  const defaultExpiryDays = branding?.defaults?.expiryDays || DEFAULT_EXPIRY_DAYS;

  const showAlert = useCallback((severity, message) => {
    setAlert({ severity, message });
  }, []);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tenantAdminAPI.listJoinLinks();
      setLinks(response.data?.links || []);
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Failed to load QR sign-up codes.');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const loadBranding = useCallback(async () => {
    try {
      const response = await tenantAdminAPI.getJoinLinkBranding();
      setBranding(response.data?.branding || null);
    } catch {
      setBranding(null);
    }
  }, []);

  useEffect(() => {
    loadLinks();
    loadBranding();
  }, [loadLinks, loadBranding]);

  const replaceLink = (updated) => {
    setLinks((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    setQrLink((current) => (current && current.id === updated.id ? updated : current));
  };

  const visibleLinks = useMemo(() => (
    showInactive ? links : links.filter((link) => ['active', 'paused', 'full'].includes(link.status))
  ), [links, showInactive]);

  const hiddenCount = links.length - visibleLinks.length;

  const groupOptions = useMemo(() => groups.map((group) => ({
    id: group.id || group._id,
    name: group.name,
  })).filter((group) => group.id), [groups]);

  const groupNameById = useMemo(
    () => new Map(groupOptions.map((group) => [group.id, group.name])),
    [groupOptions],
  );

  const domainHelper = useMemo(() => {
    const universityDomains = branding?.universityAllowedDomains || [];
    if (branding?.enforceUniversityDomains && universityDomains.length) {
      return `Leave blank to use your university's domains (${universityDomains.join(', ')}).`;
    }
    return 'Optional. Leave blank to accept any email address, or list domains like uni.ac.uk.';
  }, [branding]);

  const openCreate = () => {
    setEditingLink(null);
    setForm(buildEmptyForm(defaultExpiryDays));
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (link) => {
    setEditingLink(link);
    setForm(formFromLink(link));
    setFormError('');
    setFormOpen(true);
  };

  const handleFormChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({
      ...prev,
      [field]: field === 'groupIds' && typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleCopyLink = useCallback(async (url) => {
    if (!url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showAlert('success', 'Sign-up link copied to clipboard.');
    } catch {
      showAlert('warning', 'Unable to copy the link on this device. Select it and copy it manually.');
    }
  }, [showAlert]);

  const handleSubmit = async () => {
    if (!form.label.trim()) {
      setFormError('Give the code a label, e.g. "Year 2 – October intake".');
      return;
    }
    if (!form.expiresOn || !dayjs(form.expiresOn).isValid()) {
      setFormError('Choose an expiry date.');
      return;
    }

    const payload = {
      label: form.label.trim(),
      role: form.role,
      groupIds: form.groupIds,
      expiresAt: toExpiryIso(form.expiresOn),
      maxUses: form.maxUses === '' ? 0 : Number(form.maxUses),
      allowedDomains: form.allowedDomains,
    };

    setSaving(true);
    setFormError('');
    try {
      if (editingLink) {
        const response = await tenantAdminAPI.updateJoinLink(editingLink.id, payload);
        replaceLink(response.data.link);
        showAlert('success', 'QR code updated.');
      } else {
        const response = await tenantAdminAPI.createJoinLink(payload);
        const created = response.data.link;
        setLinks((prev) => [created, ...prev]);
        setQrLink(created);
        showAlert('success', 'QR code created. Download the poster or present it to your class.');
      }
      setFormOpen(false);
    } catch (error) {
      setFormError(error.response?.data?.message || 'Unable to save the QR code.');
    } finally {
      setSaving(false);
    }
  };

  const runLinkAction = async (link, action) => {
    setActionPending(`${action}:${link.id}`);
    try {
      let response;
      if (action === 'pause' || action === 'resume') {
        response = await tenantAdminAPI.updateJoinLink(link.id, { paused: action === 'pause' });
        showAlert('success', action === 'pause' ? 'Sign-ups paused for this code.' : 'Sign-ups resumed.');
      } else if (action === 'rotate') {
        response = await tenantAdminAPI.rotateJoinLink(link.id);
        showAlert('success', 'New code issued. The old QR code and link no longer work — download the new poster.');
      } else if (action === 'revoke') {
        response = await tenantAdminAPI.revokeJoinLink(link.id);
        showAlert('success', 'QR code revoked.');
      }
      if (response?.data?.link) {
        replaceLink(response.data.link);
        if (action === 'rotate') {
          setQrLink(response.data.link);
        }
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Unable to update the QR code.');
    } finally {
      setActionPending('');
      setConfirmAction(null);
    }
  };

  const closeMenu = () => setMenuState({ anchor: null, link: null });

  const minExpiryDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const maxExpiryDate = dayjs().add(maxExpiryDays, 'day').format('YYYY-MM-DD');

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader
        avatar={<QrIcon color="primary" />}
        title="QR sign-up codes"
        subheader="Let students join your university by scanning a code, instead of sending each one an invite."
        action={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1, mr: 1 }}>
            Create QR code
          </Button>
        )}
        sx={{ flexWrap: 'wrap', '& .MuiCardHeader-action': { alignSelf: 'center' } }}
      />
      <CardContent sx={{ pt: 0 }}>
        {alert && (
          <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        {loading && !links.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !visibleLinks.length ? (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
            }}
          >
            <QrIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {links.length ? 'No live QR codes' : 'No QR codes yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mt: 0.5 }}>
              Create a code for a cohort, then project it in a lecture or print the poster. Students scan it,
              create their account and verify their email — they are added to your university automatically.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Sign-ups</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleLinks.map((link) => {
                  const statusMeta = STATUS_META[link.status] || STATUS_META.active;
                  const isLive = ['active', 'paused', 'full'].includes(link.status);
                  const cohortNames = (link.groups || [])
                    .map((group) => group.name || groupNameById.get(group.id))
                    .filter(Boolean);
                  return (
                    <TableRow key={link.id} hover>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{link.label}</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {cohortNames.map((name) => (
                            <Chip key={name} label={name} size="small" variant="outlined" />
                          ))}
                          {link.allowedDomains?.length > 0 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="secondary"
                              label={link.allowedDomains.map((domain) => `@${domain}`).join(', ')}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{link.role}</TableCell>
                      <TableCell>{formatSignups(link)}</TableCell>
                      <TableCell>{dayjs(link.expiresAt).format('D MMM YYYY')}</TableCell>
                      <TableCell>
                        <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {isLive && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<QrIcon fontSize="small" />}
                              onClick={() => setQrLink(link)}
                              sx={{ mr: 0.5 }}
                            >
                              Show QR
                            </Button>
                            <Tooltip title="Copy link">
                              <IconButton size="small" aria-label={`Copy link for ${link.label}`} onClick={() => handleCopyLink(link.joinUrl)}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {link.status !== 'revoked' && (
                          <IconButton
                            size="small"
                            aria-label={`More actions for ${link.label}`}
                            onClick={(event) => setMenuState({ anchor: event.currentTarget, link })}
                            disabled={Boolean(actionPending)}
                          >
                            {actionPending.endsWith(link.id) ? <CircularProgress size={16} /> : <MoreIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {links.length > 0 && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch size="small" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />}
            label={(
              <Typography variant="body2" color="text.secondary">
                {`Show expired and revoked codes${hiddenCount > 0 && !showInactive ? ` (${hiddenCount})` : ''}`}
              </Typography>
            )}
          />
        )}
      </CardContent>

      <Menu anchorEl={menuState.anchor} open={Boolean(menuState.anchor)} onClose={closeMenu}>
        {menuState.link && menuState.link.status !== 'revoked' && [
          <MenuItem key="edit" onClick={() => { openEdit(menuState.link); closeMenu(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>,
          menuState.link.pausedAt ? (
            <MenuItem key="resume" onClick={() => { runLinkAction(menuState.link, 'resume'); closeMenu(); }}>
              <ListItemIcon><ResumeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Resume sign-ups</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem key="pause" onClick={() => { runLinkAction(menuState.link, 'pause'); closeMenu(); }}>
              <ListItemIcon><PauseIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Pause sign-ups</ListItemText>
            </MenuItem>
          ),
          <MenuItem key="rotate" onClick={() => { setConfirmAction({ action: 'rotate', link: menuState.link }); closeMenu(); }}>
            <ListItemIcon><RotateIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Replace code</ListItemText>
          </MenuItem>,
          <MenuItem key="revoke" onClick={() => { setConfirmAction({ action: 'revoke', link: menuState.link }); closeMenu(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><RevokeIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Revoke</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingLink ? 'Edit QR code' : 'Create a QR sign-up code'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Label"
              value={form.label}
              onChange={handleFormChange('label')}
              required
              fullWidth
              autoFocus
              inputProps={{ maxLength: 120 }}
              helperText="Shown to students and on the poster, e.g. “Year 2 – October intake”."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="join-link-role-label">Students join as</InputLabel>
                <Select
                  labelId="join-link-role-label"
                  label="Students join as"
                  value={form.role}
                  onChange={handleFormChange('role')}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Valid until"
                type="date"
                value={form.expiresOn}
                onChange={handleFormChange('expiresOn')}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minExpiryDate, max: maxExpiryDate }}
                helperText={`Up to ${maxExpiryDays} days from today.`}
              />
            </Stack>
            <FormControl fullWidth disabled={!groupOptions.length}>
              <InputLabel id="join-link-cohorts-label">Add to cohorts (optional)</InputLabel>
              <Select
                labelId="join-link-cohorts-label"
                multiple
                value={form.groupIds}
                onChange={handleFormChange('groupIds')}
                input={<OutlinedInput label="Add to cohorts (optional)" />}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {selected.map((id) => <Chip key={id} size="small" label={groupNameById.get(id) || 'Cohort'} />)}
                  </Stack>
                )}
              >
                {groupOptions.map((group) => (
                  <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                ))}
              </Select>
              {!groupOptions.length && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                  Create cohorts in the Teaching workspace to place new students into them automatically.
                </Typography>
              )}
            </FormControl>
            <TextField
              label="Maximum sign-ups"
              type="number"
              value={form.maxUses}
              onChange={handleFormChange('maxUses')}
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              helperText={editingLink
                ? `Leave blank for unlimited. ${editingLink.useCount} student(s) have used this code so far.`
                : 'Leave blank for unlimited. Set it to your cohort size to stop the code being reused.'}
            />
            <TextField
              label="Restrict to email domains"
              value={form.allowedDomains}
              onChange={handleFormChange('allowedDomains')}
              fullWidth
              placeholder="uni.ac.uk, student.uni.ac.uk"
              helperText={domainHelper}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : editingLink ? 'Save changes' : 'Create code'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmAction)} onClose={() => !actionPending && setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmAction?.action === 'rotate' ? 'Replace this QR code?' : 'Revoke this QR code?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction?.action === 'rotate'
              ? 'A new code and link will be issued with the same settings. Printed posters and shared links for the current code will stop working immediately.'
              : 'Students will no longer be able to sign up with this code. Printed posters and shared links stop working immediately. Accounts already created are not affected.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={Boolean(actionPending)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction?.action === 'revoke' ? 'error' : 'primary'}
            onClick={() => runLinkAction(confirmAction.link, confirmAction.action)}
            disabled={Boolean(actionPending)}
          >
            {confirmAction?.action === 'rotate' ? 'Replace code' : 'Revoke code'}
          </Button>
        </DialogActions>
      </Dialog>

      <JoinQrDialog
        open={Boolean(qrLink)}
        link={qrLink}
        branding={branding}
        onClose={() => setQrLink(null)}
        onCopyLink={handleCopyLink}
      />
    </Card>
  );
};

export default JoinLinksPanel;
